import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Input, Label, Spinner, Table } from "reactstrap";

import { adicionarParticipante, encerrarPropostas, listarParticipantes, publicarBid, reenviarConviteBid } from "services/bidFreteApi";
import SeletorConvidados from "../convite/SeletorConvidados";
import {
  ORIGEM_PARTICIPANTE_UI,
  PARTICIPANTE_UI,
  erroDaApi,
  fmtCnpj,
  fmtDataHora,
  fmtDataISO,
  podeEncerrarPropostas,
  podePublicar,
  podeReenviar,
  resumoParticipantes,
  soDigitos,
  statusDaEtapa,
} from "../regras";

// Aba Convites e propostas (H7 PO1-2847, só RFP). A transportadora responde
// SÓ pelo MeuFrete (revisão 5): com login na raiz recebe o link direto; sem
// login recebe, no mesmo e-mail do BID, o convite de cadastro do Hub e fica
// AGUARDANDO_CADASTRO (ou EM_CURADORIA se a matriz travou na curadoria).
// Publicar exige o pacote pronto e congela escopo e baseline.

function Badge({ tone, children, title }) {
  return (
    <span className={`lf-badge lf-badge-${tone || "neutral"}`} title={title}>
      {children}
    </span>
  );
}

const DICA_ESTADO = {
  AGUARDANDO_CADASTRO: "Sem login no MeuFrete: recebeu o convite de cadastro do Hub junto com o BID. Vira Convidado quando o login aparece.",
  EM_CURADORIA: "A matriz está na fila de curadoria da Everlog; o link sai quando a curadoria liberar.",
  EXPIRADO: "O prazo das propostas acabou sem resposta.",
};

export default function AbaConvites({ bid, empresaId, recarregar, onChanged }) {
  const [participantes, setParticipantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [busy, setBusy] = useState(null);
  const [incluindo, setIncluindo] = useState(false);
  const [selecao, setSelecao] = useState({ participantes: [], semEmail: [] });
  const [prazo, setPrazo] = useState(bid && bid.prazoPropostas ? String(bid.prazoPropostas).slice(0, 10) : "");
  const [confirmar, setConfirmar] = useState(null); // 'publicar' | 'encerrar'

  const carregar = useCallback(async () => {
    if (!empresaId || !bid) return;
    setLoading(true);
    setErro(null);
    try {
      const r = await listarParticipantes(empresaId, bid.id);
      setParticipantes(Array.isArray(r) ? r : (r && (r.items || r.participantes)) || []);
    } catch (err) {
      setErro(erroDaApi(err, "Não foi possível carregar os participantes.").mensagem);
    } finally {
      setLoading(false);
    }
  }, [empresaId, bid]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const resumo = useMemo(() => resumoParticipantes(participantes), [participantes]);
  const pubBid = useMemo(() => (bid ? { ...bid, prazoPropostas: prazo || bid.prazoPropostas } : bid), [bid, prazo]);
  const pub = podePublicar(pubBid, participantes);
  const encerravel = podeEncerrarPropostas(bid);
  const publicado = statusDaEtapa(bid, "CONVITE") === "CONCLUIDA" || (bid && bid.situacao === "PUBLICADO");
  const propostasEncerradas = statusDaEtapa(bid, "PROPOSTAS") === "CONCLUIDA";
  const prazoEtapa = ((bid && bid.etapas) || []).find((e) => e.etapa === "PROPOSTAS");

  const acao = async (chave, fn, fallback, sucesso) => {
    if (busy) return;
    setBusy(chave);
    setErro(null);
    setAviso(null);
    try {
      await fn();
      setConfirmar(null);
      if (sucesso) setAviso(sucesso);
      await carregar();
      if (recarregar) recarregar();
      if (onChanged) onChanged();
    } catch (err) {
      const e = erroDaApi(err, fallback);
      setErro(e.errors ? `${e.mensagem} ${Object.values(e.errors).join(" ")}` : e.mensagem);
    } finally {
      setBusy(null);
    }
  };

  const incluir = () => {
    if (selecao.semEmail.length) {
      setErro(`Informe o e-mail de: ${selecao.semEmail.join(", ")}.`);
      return;
    }
    if (!selecao.participantes.length) {
      setErro("Escolha ao menos uma transportadora.");
      return;
    }
    acao(
      "incluir",
      async () => {
        await adicionarParticipante(empresaId, bid.id, selecao.participantes);
        setIncluindo(false);
      },
      "Não foi possível incluir as transportadoras.",
      publicado ? "Convites enviados às novas transportadoras." : "Transportadoras incluídas. O e-mail sai ao publicar.",
    );
  };

  return (
    <>
      <div className="lf-kpis">
        <div className="lf-kpi">
          <div className="lf-kpi-label">Participantes</div>
          <div className="lf-kpi-value">{resumo.total}</div>
          <div className="lf-kpi-sub">
            {resumo.ENVIOU} enviaram · {resumo.RECUSOU} recusaram
          </div>
        </div>
        <div className="lf-kpi">
          <div className="lf-kpi-label">Cadastro pendente</div>
          <div className="lf-kpi-value">{resumo.AGUARDANDO_CADASTRO + resumo.EM_CURADORIA}</div>
          <div className="lf-kpi-sub">
            {resumo.AGUARDANDO_CADASTRO} sem login · {resumo.EM_CURADORIA} em curadoria
          </div>
        </div>
        <div className="lf-kpi">
          <div className="lf-kpi-label">Prazo das propostas</div>
          <div className="lf-kpi-value">{fmtDataISO((prazoEtapa && prazoEtapa.prazo) || bid.prazoPropostas)}</div>
          <div className="lf-kpi-sub">{propostasEncerradas ? "propostas encerradas" : publicado ? "recebendo pelo MeuFrete" : "ainda não publicado"}</div>
        </div>
      </div>

      {!publicado ? (
        <div className="lf-section">
          <div className="lf-section-title">Publicar</div>
          <div className="bf-tab-toolbar">
            <FormGroup>
              <Label>Prazo das propostas</Label>
              <Input
                type="date"
                bsSize="sm"
                value={prazo}
                onChange={(e) => {
                  const v = e.target.value;
                  setPrazo(v);
                }}
              />
            </FormGroup>
            <Button color="primary" size="sm" disabled={!pub.ok || Boolean(busy)} title={pub.motivo || ""} onClick={() => setConfirmar("publicar")}>
              Publicar e enviar convites
            </Button>
            {!pub.ok ? <span className="lf-muted">{pub.motivo}</span> : null}
          </div>
          <div className="lf-muted">Ao publicar, o escopo e a baseline ficam travados e cada participante recebe o e-mail do BID com o link do MeuFrete (ou o convite de cadastro, se não tiver login).</div>
        </div>
      ) : null}

      {confirmar === "publicar" ? (
        <div className="lf-aviso lf-aviso-warning">
          Publicar trava o escopo e a baseline e envia {resumo.total} convite(s) com prazo até {fmtDataISO(prazo)}. Continuar?{" "}
          <Button color="warning" size="sm" disabled={Boolean(busy)} onClick={() => acao("publicar", () => publicarBid(empresaId, bid.id, { prazoPropostas: prazo || undefined }), "Não foi possível publicar o BID.", "BID publicado; convites enviados.")}>
            {busy === "publicar" ? <Spinner size="sm" /> : "Confirmar"}
          </Button>{" "}
          <Button color="secondary" size="sm" outline onClick={() => setConfirmar(null)}>
            Desistir
          </Button>
        </div>
      ) : null}
      {confirmar === "encerrar" ? (
        <div className="lf-aviso lf-aviso-warning">
          Encerrar as propostas antes do prazo fecha o envio no MeuFrete para todas. Continuar?{" "}
          <Button color="warning" size="sm" disabled={Boolean(busy)} onClick={() => acao("encerrar", () => encerrarPropostas(empresaId, bid.id), "Não foi possível encerrar as propostas.", "Propostas encerradas. Rode a simulação para incluir as propostas no ranking.")}>
            {busy === "encerrar" ? <Spinner size="sm" /> : "Confirmar"}
          </Button>{" "}
          <Button color="secondary" size="sm" outline onClick={() => setConfirmar(null)}>
            Desistir
          </Button>
        </div>
      ) : null}

      {erro ? <div className="lf-aviso lf-aviso-danger">{erro}</div> : null}
      {aviso ? <div className="lf-aviso lf-aviso-info">{aviso}</div> : null}

      <div className="bf-acoes">
        {!propostasEncerradas ? (
          <Button color="info" size="sm" outline onClick={() => setIncluindo((v) => !v)} disabled={Boolean(busy)}>
            {incluindo ? "Fechar a inclusão" : "+ Incluir transportadoras"}
          </Button>
        ) : null}
        {encerravel ? (
          <Button color="warning" size="sm" outline onClick={() => setConfirmar("encerrar")} disabled={Boolean(busy)}>
            Encerrar propostas
          </Button>
        ) : null}
      </div>

      {incluindo ? (
        <div className="lf-section">
          <SeletorConvidados
            empresaId={empresaId}
            bidId={bid.id}
            excluirIds={participantes.map((p) => p.idTransportadora).filter((x) => x != null)}
            excluirCnpjs={participantes.map((p) => soDigitos(p.cnpj)).filter(Boolean)}
            onChange={setSelecao}
          />
          <div className="text-right" style={{ marginTop: 8 }}>
            <Button color="primary" size="sm" onClick={incluir} disabled={Boolean(busy)}>
              {busy === "incluir" ? <Spinner size="sm" /> : publicado ? "Incluir e enviar convite" : "Incluir"}
            </Button>
          </div>
        </div>
      ) : null}

      {loading && !participantes.length ? (
        <div className="lf-empty-state">
          <Spinner size="sm" /> Carregando os participantes...
        </div>
      ) : participantes.length === 0 ? (
        <div className="lf-empty-state">Nenhuma transportadora convidada ainda.</div>
      ) : (
        <Table className="bf-table" size="sm" responsive>
          <thead className="text-primary">
            <tr>
              <th>Transportadora</th>
              <th>Origem</th>
              <th>Estado</th>
              <th>Convite</th>
              <th>Proposta</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {participantes.map((p) => {
              const ui = PARTICIPANTE_UI[p.estado] || { label: p.estado || "—", tone: "neutral" };
              const origem = ORIGEM_PARTICIPANTE_UI[p.origem];
              return (
                <tr key={p.id || p.cnpj || p.idTransportadora}>
                  <td>
                    <strong>{p.nome || p.razaoSocial || fmtCnpj(p.cnpj)}</strong>
                    <div className="lf-muted">
                      {p.cnpj ? fmtCnpj(p.cnpj) : ""}
                      {p.email ? ` · ${p.email}` : ""}
                    </div>
                  </td>
                  <td>{origem ? origem.label : p.origem || "—"}</td>
                  <td>
                    <Badge tone={ui.tone} title={DICA_ESTADO[p.estado]}>
                      {ui.label}
                    </Badge>
                    {p.estado === "EM_CURADORIA" || p.estado === "AGUARDANDO_CADASTRO" ? <div className="lf-muted">prazo {fmtDataISO((prazoEtapa && prazoEtapa.prazo) || bid.prazoPropostas)}</div> : null}
                    {p.emailErro ? <div className="bf-text-danger" style={{ fontSize: 11 }}>e-mail falhou: {p.emailErro}</div> : null}
                  </td>
                  <td>
                    {p.conviteEm ? fmtDataHora(p.conviteEm) : "—"}
                    {p.lembreteEm ? <div className="lf-muted">lembrete {fmtDataHora(p.lembreteEm)}</div> : null}
                    {p.cadastroEm ? <div className="lf-muted">cadastro {fmtDataHora(p.cadastroEm)}</div> : null}
                  </td>
                  <td>{p.propostaEm ? fmtDataHora(p.propostaEm) : "—"}</td>
                  <td>
                    {publicado && !propostasEncerradas && podeReenviar(p) ? (
                      <button
                        type="button"
                        className="lf-btn-abrir"
                        disabled={Boolean(busy)}
                        onClick={() => acao(`reenviar-${p.id}`, () => reenviarConviteBid(empresaId, bid.id, p.id), "Não foi possível reenviar o convite.", "Convite reenviado.")}
                      >
                        {busy === `reenviar-${p.id}` ? <Spinner size="sm" /> : "Reenviar"}
                      </button>
                    ) : (
                      <span className="lf-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
}
